import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";

import OrderDeclinationTemplate from "../../templates/lookups/order-declination-reasons";
import { toast } from "react-toastify";
import {
  addOrderDeclinationReasonRequest,
  deleteOrderDeclinationRequest,
  getOrderDeclinationDetailsRequest,
  updateOrderDeclinationRequest,
  getOrderDeclinationReasonRequest,
} from "services/modules/lookups/orderDeclination";

import dayjs from "dayjs";

function OrderDeclinationPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.orderDeclinationReason.orderDeclinationReasons);
  const count = useSelector((state) => state.orderDeclinationReason.count);
  const details = useSelector((state) => state.orderDeclinationReason.orderDeclinationReason);
  const load = useSelector((state) => state.orderDeclinationReason.load);

  //permissions
  const currentUser = useSelector((state) => state.authenticate.currentUserProfile);
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.order_declination_reasons
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getOrderDeclinationDetailsRequest({ order_declination_reason: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getOrderDeclinationReasonRequest(requestData));
  };
  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY hh:mm a")}</>,
      updated_at: <>{dayjs(data?.updated_at).format("DD-MM-YYYY hh:mm a")}</>,
    };
  });
  // mappedData use for create custom cell to concat multi values or navigate button

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/order-declination-reasons",
      title: t("order_declination_reasons.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("order_declination_reasons.details")
          : id
          ? t("order_declination_reasons.edit")
          : t("order_declination_reasons.add"),
    },
  ];
  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/order-declination-reasons");
      !id && formik?.resetForm();
      // USE TOSTIFY TO CONFIRM ACTION
      toast.success(id ? t("order_declination_reasons.updated") : t("order_declination_reasons.created"));
    };
    const requestData = {
      body: {
        order_declination_reason: data,
      },
      action,
    };

    if (id) {
      //update call
      dispatch(
        updateOrderDeclinationRequest({
          ...requestData,
          order_declination_reason: id,
        })
      );
    } else {
      //create call
      dispatch(addOrderDeclinationReasonRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = () => {
    const id = selectedRow?.rowData?.id;
    //action to be called after successfull deletion
    const action = () => {
      //if list.length == 1 & page !== 1 -> setPage(page-1) & fetch
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        getList({ ...filterObj, page_number: page - 1 }); //temporary until the backend sends the data correctly so I can update the state instead of re-fetching
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/order-declination-reasons");
      toast.success(t("order_declination_reasons.deleted"));
    };
    const requestData = {
      order_declination_reasons: id,
      action, //dispatched on success
    };
    dispatch(deleteOrderDeclinationRequest(requestData));
    setOpenDeleteModal(false);
  };

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  //listing all tags
  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  //details of a single tag
  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <OrderDeclinationTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={isSuperAdmin || permissions?.can_create}
      />
    </>
  );
}

export default OrderDeclinationPage;
